import { useState, useEffect, useRef } from 'react';
import {
  useModalTableMultiselectStates,
  ModalTableMultiselectStates,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import { PAGINATION_PARAMS } from '@lib/enums/urls';
import { Values } from '@lib/interfaces/form';
import { TableTabs } from '../enums';

interface UseModalTableMultiselectProps {
  isOpen: boolean;
  selected?: string[];
  defaultTab?: TableTabs;
  filterStateDefault: Values;
}

interface UseModalTableMultiselectReturn extends ModalTableMultiselectStates {
  selectedIdsForQuery: string[];
  setSelectedIdsForQuery: (ids: string[]) => void;
  idsForSelectedTabQuery: string[];
}

export default function useModalTableMultiselect({
  isOpen,
  selected,
  filterStateDefault,
}: UseModalTableMultiselectProps): UseModalTableMultiselectReturn {
  const states = useModalTableMultiselectStates({
    filterStateDefault,
    selected,
  });
  const {
    activeTabState,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    filterState,
    filterStateSelected,
  } = states;
  const [activeTab, setActiveTab] = activeTabState;
  const [, setPagination] = paginationState;
  const [, setPaginationSelected] = paginationStateSelected;
  const [, onSearchHandler] = searchState;
  const [, onSearchHandlerSelected] = searchStateSelected;
  const [, setFilterValues] = filterState;
  const [filterValuesSelectedState, setFilterValuesSelected] =
    filterStateSelected;

  const [selectedIdsForQuery, setSelectedIdsForQuery] = useState<string[]>(
    selected || [],
  );
  const [idsForSelectedTabQuery, setIdsForSelectedTabQuery] = useState<
    string[]
  >([]);
  const prevActiveTabRef = useRef(activeTab);
  const hasSnapshotedSelectedTab = useRef(false);
  const prevIsOpenRef = useRef(isOpen);
  const prevSelectedRef = useRef(selected);

  // Reset states when modal opens or selected prop changes
  useEffect(() => {
    const isModalOpening = !prevIsOpenRef.current && isOpen;
    const selectedChanged =
      isOpen &&
      JSON.stringify(prevSelectedRef.current) !== JSON.stringify(selected);

    if (isModalOpening || selectedChanged) {
      // Reset all states to trigger fresh data fetch
      setPagination(PAGINATION_PARAMS);
      setPaginationSelected(PAGINATION_PARAMS);
      onSearchHandler('');
      onSearchHandlerSelected('');
      // const defaultFilters = { ...(filterStateDefault || {}) };
      // setFilterValues(defaultFilters);
      // setFilterValuesSelected({ ...(filterStateDefault || {}) });
      setIdsForSelectedTabQuery([]);
      hasSnapshotedSelectedTab.current = false;

      // Sync selectedIdsForQuery
      if (selected) {
        setSelectedIdsForQuery([...selected]);
      } else {
        setSelectedIdsForQuery([]);
      }
    }

    prevIsOpenRef.current = isOpen;
    prevSelectedRef.current = selected;
  }, [
    isOpen,
    selected,
    setPagination,
    setPaginationSelected,
    onSearchHandler,
    onSearchHandlerSelected,
    setFilterValues,
    setFilterValuesSelected,
    filterStateDefault,
  ]);

  // Reset pagination when switching tabs
  useEffect(() => {
    if (prevActiveTabRef.current !== activeTab) {
      setPagination((p) => ({ ...p, page: '1' }));
      setPaginationSelected((p) => ({ ...p, page: '1' }));
      prevActiveTabRef.current = activeTab;
    }
  }, [activeTab, setPagination, setPaginationSelected]);

  // Snapshot IDs for the "Selected" tab query only when switching to Selected tab
  // This prevents refetching when items are unselected while viewing the Selected tab
  useEffect(() => {
    if (isOpen && activeTab === TableTabs.Selected) {
      if (!hasSnapshotedSelectedTab.current) {
        // First time on Selected tab - take snapshot
        setIdsForSelectedTabQuery(selectedIdsForQuery);
        hasSnapshotedSelectedTab.current = true;
      } else if (
        idsForSelectedTabQuery.length === 0 &&
        selectedIdsForQuery.length > 0
      ) {
        // Special case: if we switched to Selected tab before allIds loaded (empty snapshot),
        // update the snapshot when the IDs arrive
        setIdsForSelectedTabQuery(selectedIdsForQuery);
      }
    }
  }, [isOpen, activeTab, selectedIdsForQuery, idsForSelectedTabQuery.length]);

  // Update snapshot when on All tab and selection changes
  // This ensures Selected tab reflects changes made from the All tab
  useEffect(() => {
    if (isOpen && activeTab === TableTabs.All) {
      setIdsForSelectedTabQuery(selectedIdsForQuery);
      hasSnapshotedSelectedTab.current = false;
    }
  }, [isOpen, activeTab, selectedIdsForQuery]);

  // Re-trigger Selected tab query when its filters change while the tab is active
  useEffect(() => {
    if (isOpen && activeTab === TableTabs.Selected) {
      setIdsForSelectedTabQuery((ids) => [...ids]);
    }
  }, [isOpen, activeTab, filterValuesSelectedState]);

  // Reset activeTab when modal closes
  // Other states are reset when modal opens (to trigger fresh data fetch)
  useEffect(() => {
    if (!isOpen) {
      setActiveTab(TableTabs.All);
    }
  }, [isOpen, setActiveTab]);

  return {
    ...states,
    selectedIdsForQuery,
    setSelectedIdsForQuery,
    idsForSelectedTabQuery,
  };
}
